
import { useState, useEffect } from "react";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ExerciseHeader } from "./ExerciseHeader";
import { SetData } from "./types";
import { VideoSection } from "./components/VideoSection";
import { ExerciseProgress } from "./components/ExerciseProgress";
import { GripVertical } from "lucide-react";

interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number;
  set_data: SetData[];
  video_url?: string;
  position: number;
  exercise_rest_time?: number;
  set_rest_time?: number;
}

interface ExerciseItemProps {
  exercise: Exercise;
  onEdit: (exercise: Exercise) => void;
  onDelete: (id: string) => void;
  onUpdate: (exerciseId: string, field: string, value: number | SetData[] | string) => void;
  showControls?: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  routineId?: string;
  isPersonalPlan?: boolean;
}

export const ExerciseItem = ({
  exercise,
  onEdit,
  onDelete,
  onUpdate,
  showControls = true,
  isDragging,
  dragHandleProps,
  isCollapsed = false,
  onCollapseChange,
  routineId,
  isPersonalPlan = false
}: ExerciseItemProps) => {
  const [videoUrl, setVideoUrl] = useState(exercise.video_url || "");
  const [localCollapsed, setLocalCollapsed] = useState(isCollapsed);

  // Sincronizar con el estado persistente cuando cambie
  useEffect(() => {
    setLocalCollapsed(isCollapsed);
  }, [isCollapsed]);

  const handleVideoUpdate = (exerciseId: string, field: string, value: string) => {
    setVideoUrl(value);
    onUpdate(exerciseId, field, value);
  };

  const handleCollapseChange = (open: boolean) => {
    const collapsed = !open;
    setLocalCollapsed(collapsed);
    
    // Persistir el cambio de estado
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
    
    console.log(`🔄 Exercise ${exercise.id} collapse state changed to: ${collapsed}`);
  };

  const isOpen = !localCollapsed;
  const canDrag = showControls && dragHandleProps;

  console.log(`💪 ExerciseItem ${exercise.id} - Personal Plan: ${isPersonalPlan}, Can Drag: ${canDrag}`);

  return (
    <div 
      className={`
        bg-black/10 dark:bg-white/10 backdrop-blur-lg rounded-xl p-3 sm:p-4 relative
        ${isDragging ? 'ring-2 ring-[#00C1D6] bg-white/20 shadow-2xl scale-105' : 'hover:bg-black/15'}
        ${canDrag ? 'pl-12 sm:pl-14' : ''}
        transition-all duration-200 ease-in-out
      `}
      style={{
        transform: isDragging ? 'rotate(2deg)' : 'none',
        zIndex: isDragging ? 1000 : 'auto'
      }}
    >
      {/* Drag Handle - Más prominente y siempre visible en plan personal */}
      {canDrag && (
        <div 
          {...dragHandleProps}
          className={`
            absolute left-2 top-1/2 transform -translate-y-1/2 z-20 
            cursor-grab active:cursor-grabbing p-2 rounded-md 
            ${isDragging ? 'bg-[#00C1D6]/30' : 'hover:bg-white/20'}
            transition-all duration-200
            ${localCollapsed ? 'bg-white/10' : 'hover:bg-white/15'}
          `}
          style={{
            touchAction: 'none'
          }}
        >
          <GripVertical 
            className={`
              h-5 w-5 text-primary
              ${isDragging ? 'text-[#00C1D6] scale-110' : 'opacity-70 hover:opacity-100'}
              transition-all duration-200
            `} 
          />
        </div>
      )}

      <Collapsible 
        open={isOpen} 
        onOpenChange={handleCollapseChange}
      >
        {/* Header */}
        <div className="flex justify-between items-center gap-2">
          <div className="flex-1">
            <ExerciseHeader
              id={exercise.id}
              name={exercise.name}
              isOpen={isOpen}
              onEdit={() => onEdit(exercise)}
              onDelete={() => onDelete(exercise.id)}
              showControls={showControls}
            />
          </div>
        </div>
        
        <CollapsibleContent className="mt-3 sm:mt-4">
          {/* Pass isPersonalPlan to ExerciseProgress */}
          <ExerciseProgress
            exercise={{
              ...exercise,
              exercise_rest_time: exercise.exercise_rest_time,
              set_rest_time: exercise.set_rest_time
            }}
            onUpdate={onUpdate}
            isPersonalPlan={isPersonalPlan}
          />
          
          <VideoSection
            exerciseId={exercise.id}
            videoUrl={videoUrl}
            onUpdate={handleVideoUpdate}
          />
        </CollapsibleContent>
      </Collapsible>

      
    </div>
  );
};
